/**
 * Who you are signed in as — a small card at the top of Home's right column
 * (ADR-0070): initials, name, role, email and last sign-in, and the way to the
 * full profile at `/me`. Not the full profile: tenant, ids and grants stay
 * there.
 */

import { Anchor, Avatar, Group, Paper, Stack, Text } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";
import { initials } from "@/shared/ui/initials";

const SIGNED_IN = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

export interface ProfileCardPerson {
  display_name: string;
  email: string;
  role: string | null;
  last_login_at_utc: string | null;
}

export function ProfileCard({ me }: { me: ProfileCardPerson }) {
  return (
    <Paper withBorder radius="md" p="lg">
      <Group gap="md" wrap="nowrap" align="flex-start">
        <Avatar size={44} color="brand" radius="xl">
          {initials(me.display_name)}
        </Avatar>
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text fw={600} truncate>
            {me.display_name}
          </Text>
          <Text size="sm" c="dimmed" tt="capitalize">
            {me.role ?? "No role"}
          </Text>
          <Text size="sm" c="dimmed" truncate>
            {me.email}
          </Text>
          {me.last_login_at_utc && (
            <Text size="xs" c="dimmed">
              Last sign-in {SIGNED_IN.format(new Date(me.last_login_at_utc))}
            </Text>
          )}
        </Stack>
      </Group>
      <Group justify="flex-end" mt="sm">
        <Anchor component={RouterLink} to="/me" size="sm">
          <Group gap={4}>
            View profile <IconArrowRight size={14} />
          </Group>
        </Anchor>
      </Group>
    </Paper>
  );
}
